import React, { createContext, useState, useEffect, ReactNode, useContext, Dispatch, SetStateAction } from 'react';
import { objectCreationCache } from '../utils/objectCreationCache';
import { getTenantStorageNamespace } from '../utils/domainUtils';
import { getUserDisplayNameFromToken } from '../utils/userDisplayName';
import TokenManager from './tokenManager';

interface UserContextType {
  user: string;
  refreshToken: string;
  rememberMe: boolean;
  username: string;
  setUserContext: (
    userData: string,
    options?: { refreshToken?: string; remember?: boolean; username?: string }
  ) => void;
  clearUserContext: () => void;
}

/** Datos de empresa en estado (localStorage); sin métodos del contexto. */
interface CompanyState {
  id: string;
  name: string;
  features: { [key: string]: boolean | string | number | null | undefined };
  objects: ObjectType[];
}

interface CompanyContextType extends CompanyState {
  setCompanyContext: (companyData: Partial<CompanyState>) => void;
  setObjects: (objects: ObjectType[]) => void;
  clearCompanyContext: () => void;
}

interface BrandContextType {
  logo: string;
  favicon: string;
  brandName: string;
  setLogo: Dispatch<SetStateAction<string>>;
  setFavicon: Dispatch<SetStateAction<string>>;
  setBrandName: Dispatch<SetStateAction<string>>;
}


export const UserContext = createContext<UserContextType | undefined>(undefined);
export const CompanyContext = createContext<CompanyContextType | undefined>(undefined);
export const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<string>(() => {
    const storedUser = TokenManager.getIdToken();
    return storedUser ? storedUser : '';
  });
  const [refreshToken, setRefreshToken] = useState<string>(() => localStorage.getItem('refreshToken') || '');
  const [rememberMe, setRememberMe] = useState<boolean>(() => localStorage.getItem('rememberMe') === 'true');
  const [username, setUsername] = useState<string>(() => {
    const storedUsername = localStorage.getItem('userEmail') || '';
    if (storedUsername) return storedUsername;

    const storedUser = TokenManager.getIdToken();
    return getUserDisplayNameFromToken(storedUser);
  });

  useEffect(() => {
    const handleTokenUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ idToken?: string; refreshToken?: string; remember?: boolean; username?: string }>).detail || {};
      if (detail.idToken !== undefined) {
        setUser(detail.idToken);
      }
      if (detail.refreshToken !== undefined) {
        setRefreshToken(detail.refreshToken);
      }
      if (detail.remember !== undefined) {
        setRememberMe(detail.remember);
      }
      if (detail.username !== undefined) {
        setUsername(detail.username || getUserDisplayNameFromToken(detail.idToken));
      } else if (detail.idToken) {
        const displayName = getUserDisplayNameFromToken(detail.idToken);
        if (displayName) setUsername(displayName);
      }
    };

    window.addEventListener('orgcloud:token-updated', handleTokenUpdate as EventListener);
    return () => window.removeEventListener('orgcloud:token-updated', handleTokenUpdate as EventListener);
  }, []);

  const setUserContext = (
    userData: string,
    options?: { refreshToken?: string; remember?: boolean; username?: string }
  ) => {
    // Los guards de ruta consultan TokenManager/localStorage durante el mismo
    // cambio de ruta. Persistir antes del setState evita un rebote al login.
    const resolvedUsername = options?.username || getUserDisplayNameFromToken(userData);
    TokenManager.storeSession({
      idToken: userData,
      refreshToken: options?.refreshToken,
      remember: options?.remember,
      username: options?.username !== undefined ? resolvedUsername : undefined,
    });
    setUser(userData);
    if (options) {
      if (options.refreshToken !== undefined) {
        setRefreshToken(options.refreshToken);
      }
      if (options.remember !== undefined) {
        setRememberMe(options.remember);
      }
      if (options.username !== undefined) {
        setUsername(resolvedUsername);
      }
    } else if (resolvedUsername) {
      setUsername(resolvedUsername);
    }
  };

  const clearUserContext = () => {
    setUser('');
    setRefreshToken('');
    setRememberMe(false);
    setUsername('');
    TokenManager.clearSession();
  };

  return (
    <UserContext.Provider value={{ user, refreshToken, rememberMe, username, setUserContext, clearUserContext }}>
      {children}
    </UserContext.Provider>
  );
};

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  // Función helper para obtener clave específica por cliente
  const getCompanyStorageKey = (clientId?: string): string => {
    const tenantNamespace = getTenantStorageNamespace();
    
    const key = clientId || tenantNamespace;
    return `${key}_company`;
  };

  const [company, setCompany] = useState<CompanyState>(() => {
    // Verificar si hay un cambio de cliente pendiente
    const selectedClientId = localStorage.getItem('selectedClientId');
    const lastClientId = localStorage.getItem('lastClientId');
    
    console.log("[CompanyProvider Init] selectedClientId:");
    
    // Si hay un cambio de cliente detectado, limpiar datos del anterior
    if (selectedClientId && lastClientId && selectedClientId !== lastClientId) {
      console.log("🧹 [CompanyProvider Init] Cambio de cliente detectado al inicializar: [redacted] -> [redacted]");
      
      // Limpiar datos del cliente anterior
      const oldKey = getCompanyStorageKey(lastClientId);
      localStorage.removeItem(oldKey);
      localStorage.removeItem('company'); // Legacy
      
      // Limpiar cualquier caché relacionado
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key !== 'selectedClientId' && key !== 'lastClientId' && (
          key.includes('_company') || 
          key.includes('_objects') || 
          key.includes('_dashboards')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        console.log("🧹 [CompanyProvider Init] Limpiando caché: [redacted]");
        localStorage.removeItem(key);
      });
      
      // Actualizar lastClientId
      localStorage.setItem('lastClientId', selectedClientId);
      
      // Limpiar selectedClientId ya que fue procesado
      localStorage.removeItem('selectedClientId');
      
      // Retornar estado vacío para forzar recarga completa
      return { id: '', name: '', features: {}, objects: [] };
    }
    
    // Si hay selectedClientId pero no entró al branch de cambio de cliente:
    // NO borrar sin más: si aún no hay lastClientId, promover selected → last (multi-cliente / selector de org).
    if (selectedClientId) {
      const tid = selectedClientId.trim();
      const lid = lastClientId?.trim();
      if (tid && !lid) {
        console.log("[CompanyProvider Init] Promoviendo selectedClientId → lastClientId:");
        localStorage.setItem('lastClientId', tid);
      } else {
        console.log('[CompanyProvider Init] Limpiando selectedClientId después de procesarlo');
      }
      localStorage.removeItem('selectedClientId');
    }
    
    // Cargar empresa: priorizar el bucket del último cliente (alineado con GET accessClient + multi-cliente)
    const currentSubdomain = getTenantStorageNamespace();
    const lastId = localStorage.getItem('lastClientId')?.trim();
    let storedCompany: string | null = null;
    if (lastId) {
      storedCompany = localStorage.getItem(getCompanyStorageKey(lastId));
    }
    if (!storedCompany) {
      storedCompany =
        localStorage.getItem(getCompanyStorageKey()) ||
        localStorage.getItem(getCompanyStorageKey(currentSubdomain));
    }

    if (storedCompany) {
      try {
        const parsed = JSON.parse(storedCompany) as CompanyState;
        if (parsed?.id) {
          localStorage.setItem('lastClientId', parsed.id);
        }
        return parsed;
      } catch {
        return { id: '', name: '', features: {}, objects: [] };
      }
    }
    return { id: '', name: '', features: {}, objects: [] };
  });

  // Guardar en localStorage usando clave específica del cliente
  useEffect(() => {
    if (company.id) {
      // Usar el ID del cliente actual para la clave
      const key = getCompanyStorageKey(company.id);
      localStorage.setItem(key, JSON.stringify(company));
      
      // También guardar el último clientId usado para detectar cambios
      localStorage.setItem('lastClientId', company.id);
    }
  }, [company]);

  // Detectar cambio de cliente y limpiar datos del anterior
  useEffect(() => {
    const lastClientId = localStorage.getItem('lastClientId');
    const currentClientId = company.id;
    
    if (lastClientId && currentClientId && lastClientId !== currentClientId) {
      console.log("🧹 Cambio de cliente detectado: [redacted] -> [redacted]");
      // Limpiar datos del cliente anterior
      const oldKey = getCompanyStorageKey(lastClientId);
      localStorage.removeItem(oldKey);
      
      // También limpiar clave legacy sin prefijo
      localStorage.removeItem('company');
      
      // Limpiar el caché de objetos creados
      objectCreationCache.clear();
      console.log('🗑️ Caché de objetos limpiado al cambiar de cliente (CompanyProvider)');
    }
  }, [company.id]);

  const setCompanyContext = (companyData: Partial<CompanyState>) => {
    setCompany(prevCompany => {
      // Si el ID del cliente está cambiando, limpiar datos del anterior
      if (companyData.id && prevCompany.id && companyData.id !== prevCompany.id) {
        console.log("🧹 Cambio de cliente detectado: [redacted] -> [redacted]");
        // Limpiar datos del cliente anterior
        const oldKey = getCompanyStorageKey(prevCompany.id);
        localStorage.removeItem(oldKey);
        localStorage.removeItem('company'); // Legacy
      }
      
      return {
        ...prevCompany,
        ...companyData,
      };
    });
  };

  const clearCompanyContext = () => {
    console.log('🧹 Limpiando Company Context');
    
    // Limpiar datos del cliente actual
    if (company.id) {
      const key = getCompanyStorageKey(company.id);
      localStorage.removeItem(key);
    }
    
    // Limpiar también por subdominio (por si acaso)
    const currentSubdomain = getTenantStorageNamespace();
    localStorage.removeItem(getCompanyStorageKey(currentSubdomain));
    
    // Limpiar clave legacy
    localStorage.removeItem('company');
    localStorage.removeItem('lastClientId');
    
    // Resetear el estado (solo las propiedades de datos, no las funciones)
    setCompany(prevCompany => ({
      ...prevCompany,
      id: '',
      name: '',
      features: {},
      objects: []
    }));
  };

  const setObjects = (objects: ObjectType[]) => {
    // Validar que objects sea un array antes de procesarlo
    if (!Array.isArray(objects)) {
      console.error("Error: setObjects recibió un valor que no es un array:");
      setCompany(prevCompany => ({
        ...prevCompany,
        objects: [],
      }));
      return;
    }
    
    const formattedObjects: ObjectType[] = objects.map(obj => ({
      ClientId: obj.ClientId,
      EntryId: obj.EntryId,
      Fields: obj.Fields?.map(field => ({
        EntryId: field.EntryId,
        ApiName: field.ApiName,
        Name: field.Name,
        Description: field.Description,
        Type: field.Type,
        EntryIdReferenced: field.EntryIdReferenced,
        isRequired: field.isRequired,
        Choices: field.Choices,
        isMain: field.isMain,
        isSubscription: field.isSubscription ?? false,
      })) || [], // Si `Fields` no está definido, asignamos un arreglo vacío
      Information: {
        ApiName: obj.Information?.ApiName || '',
        CreatedBy: obj.Information?.CreatedBy || '',
        DateCreated: obj.Information?.DateCreated || '',
        DateModified: obj.Information?.DateModified || '',
        Description: obj.Information?.Description || '',
        ModifiedBy: obj.Information?.ModifiedBy || '',
        Name: obj.Information?.Name || '',
        Extended: obj.Information?.Extended || '',
      },
      Steps: obj.Steps?.map(step => ({
        StepId: step.StepId, // Asegurándonos de que StepId esté presente
        Name: step.Name || '',
        Description: step.Description || '',
        Fields: step.Fields?.map(field => ({
          EntryId: field.EntryId,   // Solo guardamos EntryId, Name, Placeholder, y agregamos Type
          Name: field.Name || '',
          Placeholder: field.Placeholder || '',
          Type: field.Type || '', // Agregamos un valor predeterminado para Type
        })) || [], // Si `Fields` no está definido, asignamos un arreglo vacío
      })) || [], // Si no existe Steps en el objeto original, asignamos un arreglo vacío
    }));
  
    setCompany(prevCompany => ({
      ...prevCompany,
      objects: formattedObjects,
    }));
  };  

  return (
    <CompanyContext.Provider value={{ ...company, setCompanyContext, setObjects, clearCompanyContext }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const BrandProvider = ({ children }: { children: ReactNode }) => {
  // Importar utilidades de storage específico por subdominio
  const getStorageKey = (key: string): string => {
    const subdomain = getTenantStorageNamespace();
    return `${subdomain}_${key}`;
  };

  const [logo, setLogo] = useState<string>(() => localStorage.getItem(getStorageKey('brandLogo')) || '');
  const [favicon, setFavicon] = useState<string>(() => localStorage.getItem(getStorageKey('brandFavicon')) || '');
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem(getStorageKey('brandName')) || '');

  // Limpiar datos del subdominio anterior cuando cambia
  useEffect(() => {
    const currentSubdomain = getStorageKey('').replace('_', '');
    const storedSubdomain = localStorage.getItem('currentSubdomain');
    
    if (storedSubdomain && storedSubdomain !== currentSubdomain) {
      console.log("🧹 Cambio de subdominio detectado: [redacted] -> [redacted]");
      // Limpiar datos del anterior subdominio
      const keysToClean = ['brandLogo', 'brandFavicon', 'brandName', 'brandLogoUrl'];
      keysToClean.forEach(key => {
        localStorage.removeItem(`${storedSubdomain}_${key}`);
        localStorage.removeItem(key); // También limpiar claves legacy sin prefijo
      });
      
      // Resetear el estado actual para forzar recarga
      setLogo('');
      setFavicon('');
      setBrandName('');
    }
    
    localStorage.setItem('currentSubdomain', currentSubdomain);
  }, []); // Solo ejecutar una vez al montar

  useEffect(() => {
    if (logo) {
      localStorage.setItem(getStorageKey('brandLogo'), logo);
    }
  }, [logo]);

  useEffect(() => {
    if (favicon) {
      localStorage.setItem(getStorageKey('brandFavicon'), favicon);
    }
  }, [favicon]);

  useEffect(() => {
    if (brandName) {
      localStorage.setItem(getStorageKey('brandName'), brandName);
    }
  }, [brandName]);

  return (
    <BrandContext.Provider value={{ logo, favicon, brandName, setLogo, setFavicon, setBrandName }}>
      {children}
    </BrandContext.Provider>
  );
};


export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export const useCompanyContext = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompanyContext must be used within a CompanyProvider');
  }
  return context;
};

export const useBrandContext = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrandContext must be used within a BrandProvider');
  }
  return context;
};
